import {ReactElement, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import useMounted from '../../../../../hooks/use-mounted/use-mounted';
import {emit} from '../../../../../utils/events';
import ActionText from '../../../../atoms/ActionText/ActionText';
import Accordion from '../../../Accordion/Accordion';
import InfoIcon from '../../../InfoIcon/InfoIcon';
import {StyledHeader, StyledText} from './styles';
import Props, {RenderDataProps} from './typings';

const errorMsg = 'Sorry, but shipping information is not available right now, try again later.';

// TODO: once system is moved to APP, revert some of the changes as dynamic import will be no longer used
const Shipping: React.FC<Props> = ({
  productSlug,
  productType,
  artistName,
  artworkName,
  defaultCollapsed = false,
  collapsible = false,
  reduxStates,
  reduxActions,
}) => {
  // Hooks
  const dispatch = useDispatch();
  const hasMounted = useMounted();
  const [render, setRender] = useState<RenderDataProps>({});

  // Variables
  const [showError, setShowError] = useState(false);

  // Redux selectors
  const {shipping: shippingData} = useSelector(reduxStates.product);
  const {freeShipping: promoFreeShipping} = useSelector(reduxStates.product);
  const settings = useSelector(reduxStates.settings);

  const shipping = {
    original: {
      shipTo: shippingData.original.shipping_to,
      dispatchFrom: shippingData.original.shipping_from,
      shipPrice: shippingData.original.user_entry?.price_display,
      dispatchTime: shippingData.original.dispatch_days,
      freeShipping: promoFreeShipping,
      packageType: shippingData.original.package_type,
    },
    prints: {
      shipTo: shippingData.prints[productSlug]?.shipping_to,
      dispatchFrom: shippingData.prints[productSlug]?.shipping_from,
      shipPrice: shippingData.prints[productSlug]?.user_entry?.price_display,
      dispatchTime: shippingData.prints[productSlug]?.dispatch_days,
      freeShipping: shippingData.prints[productSlug]?.user_entry?.is_free,
      packageType: shippingData.prints[productSlug]?.package_type,
    },
  };

  const errorCallbacks = [
    () => {
      setShowError(true);
    },
  ];

  const successCallbacks = [
    () => {
      setShowError(false);
    },
  ];

  const cleanupCallbacks = [];

  const getShippingData = () => {
    if (!productSlug) return;

    dispatch(
      reduxActions.get({
        slug: productSlug,
        type: productType,
        successCallbacks,
        errorCallbacks,
        cleanupCallbacks,
      }),
    );
  };

  const clearShippingData = () => {
    dispatch(reduxActions.clear());
  };

  // Either fill data to render or request for new data if there is none
  useEffect(() => {
    if (!hasMounted) return;

    if (!Object.keys(shippingData.original).length && !Object.keys(shippingData.prints).length) {
      getShippingData();
    } else {
      setRender({
        shipTo: shipping[productType].shipTo,
        dispatchFrom: shipping[productType].dispatchFrom,
        shipPrice: shipping[productType].shipPrice,
        dispatchTime: shipping[productType].dispatchTime,
        packageType: shipping[productType].packageType,
        freeShipping: shipping[productType].freeShipping,
      });
    }
  }, [shippingData]);

  // Request new shipping data if selected product changed
  useEffect(() => {
    if (!productSlug) return;

    getShippingData();
  }, [productSlug]);

  // Clear shipping data if settings were changed, done to clear cache
  useEffect(() => {
    if (!hasMounted) return;

    clearShippingData();
  }, [settings.country_display, settings.currency]);

  const shippingRequestClick = () => {
    emit('CLICK_CONTACT_ARTIST', `Request to ship ${artworkName} to ${render.shipTo}`);
  };

  const header = (): ReactElement => (
    <StyledHeader>
      <h4>Delivery</h4>
      <InfoIcon
        size={17}
        text={`This artwork is sold and shipped to you by ${artistName}${
          render.packageType ? `, and will be packaged ${render.packageType.toLowerCase()}` : ''
        }.`}
      />
    </StyledHeader>
  );

  const body = (): ReactElement => (
    <>
      <StyledText>Shipping to {render.shipTo}</StyledText>
      <StyledText $strong $bottomMargin>
        {render.freeShipping === true ? 'Free' : render.shipPrice}
      </StyledText>
      <StyledText>Dispatches from {render.dispatchFrom} within</StyledText>
      {render.dispatchTime && (
        <StyledText $strong>
          {render.dispatchTime} {render.dispatchTime > 1 ? 'working days' : 'working day'}
        </StyledText>
      )}
    </>
  );

  const noShippingMsg = (): ReactElement => (
    <>
      <StyledText>
        Unfortunately {artistName} does not ship to {render.shipTo}.
      </StyledText>
      <ActionText
        onClick={shippingRequestClick}
        fontSize={14}
        children={`Ask ${artistName} to dispatch to your country.`}
      />
    </>
  );

  return Object.keys(render).length > 0 || showError ? (
    <Accordion
      title={header()}
      defaultCollapsed={defaultCollapsed}
      collapsible={collapsible}
      hasDividers
      strongTitle={false}
    >
      {showError ? (
        <StyledText>{errorMsg}</StyledText>
      ) : render.shipPrice ? (
        body()
      ) : (
        noShippingMsg()
      )}
    </Accordion>
  ) : (
    <></>
  );
};

export default Shipping;
